/* eslint-disable no-restricted-imports */
import { useFormik } from 'formik';
import { forwardRef, useCallback, useImperativeHandle } from 'react';
import { TiDelete as DeleteIcon } from 'react-icons/ti';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { object, string } from 'yup';

import { Tooltip, UploadDiskIcon } from '@frontend/components';

import { Input } from 'components/Forms/Inputs';
import UploadButton from 'components/UploadButton/UploadButton';
import Radio from 'components/lib/Radio/Radio';

import { PAYMENT_METHOD } from 'constants/all';

import { getOrder } from 'store/orders/selectors';
import { hasCreditCardCheckoutEnabled } from 'store/session/selectors';

import { DRAWER_MODES } from '../../constants';
import { setPaymentMethodOld as setPaymentMethod } from '../../data/actions';
import {
  getAccount,
  getDrawerMode,
  getPaymentMethod,
  getPurchaseOrderFile,
  getPurchaseOrderNumber,
  getSubtotalCost,
} from '../../data/reducer';

const RadioGroup = Radio.Group;

const AttachPurchaseOrder = styled.div`
  padding-bottom: 2px;
  padding-top: 10px;
  line-height: 32px;
`;

const StyledRadioGroup = styled(RadioGroup)`
  display: flex;
  flex-direction: column;
  ${(props) =>
    props.value === PAYMENT_METHOD.PURCHASE_ORDER && 'padding-bottom: 22px;'};
`;

const StyledRadio = styled(Radio)`
  margin-bottom: 5px;
`;

const PaymentDetails = styled.div`
  border-top: 1px solid #ddd;
  display: flex;
  flex-direction: column;
  margin-left: -22px;
  margin-right: -22px;
  padding-left: 22px;
  padding-right: 22px;
  padding-top: 22px;
`;

const PurchaseOrderFile = styled.span`
  span {
    text-decoration: underline;
  }
  button {
    position: relative;
    top: -1px;
    margin-left: 3px;
    color: #8d8d8d;
    cursor: pointer;
  }
`;

const SectionHeader = styled.div`
  color: #9b9b9b;
  font-weight: bold;
  size: 12px;
`;

const FieldColumn = styled.div`
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  justify-content: space-around;
`;

const FieldWrapper = styled.div`
  width: 200px;
  .ant-row {
    align-items: flex-start;
    display: flex;
    flex-direction: column;
  }
  .ant-form-item {
    margin-bottom: 0px;
  }
`;

const UploadButtonWrapper = styled.div`
  display: flex;
`;

const PaymentMethodForm = forwardRef<HTMLFormElement>((_, ref) => {
  const dispatch = useDispatch();
  const account = useSelector(getAccount);
  const creditCardCheckoutEnabled = useSelector(hasCreditCardCheckoutEnabled);
  const paymentMethod = useSelector(getPaymentMethod);
  const purchaseOrderNumber = useSelector(getPurchaseOrderNumber);
  const purchaseOrderFile = useSelector(getPurchaseOrderFile);
  const subTotal = useSelector(getSubtotalCost);
  const drawerMode = useSelector(getDrawerMode);
  const order = useSelector(getOrder);

  const setData = useCallback(
    (data: any) => {
      dispatch(setPaymentMethod(data));
    },
    [dispatch],
  );

  const {
    values,
    submitForm,
    setFieldValue,
    handleSubmit,
    handleChange,
    touched,
    errors,
  } = useFormik({
    initialValues: {
      paymentMethod:
        paymentMethod === undefined ? PAYMENT_METHOD.NONE : paymentMethod,
      purchaseOrderNumber,
      purchaseOrderFile,
    },
    validationSchema: object().shape({
      paymentMethod: string().nullable(true),
      purchaseOrderNumber: string().when('paymentMethod', {
        is: PAYMENT_METHOD.PURCHASE_ORDER,
        then: string().required('This field is required'),
        otherwise: string().notRequired(),
      }),
    }),
    onSubmit: setData,
  });

  useImperativeHandle(ref, () => ({
    submit: submitForm,
  }));

  const handleAttach = (file) => {
    setFieldValue('purchaseOrderFile', file);
  };

  const handleRemoveAttachment = () => {
    setFieldValue('purchaseOrderFile', null);
  };
  return (
    <form autoComplete="none" onSubmit={handleSubmit}>
      <StyledRadioGroup
        name="paymentMethod"
        value={values.paymentMethod}
        onChange={handleChange}
        disabled={drawerMode === DRAWER_MODES.EDIT}
      >
        <StyledRadio value={PAYMENT_METHOD.NONE}>Not Provided</StyledRadio>
        <Tooltip
          placement="left"
          label={
            !account
              ? 'To facilitate this order with a Purchase Order, associate this contact with an account.'
              : account.creditLine && subTotal > account.creditLine
              ? 'Order subtotal exceeds credit line for account.'
              : null
          }
        >
          <div>
            <StyledRadio
              value={PAYMENT_METHOD.PURCHASE_ORDER}
              disabled={!account}
            >
              Purchase Order
            </StyledRadio>
          </div>
        </Tooltip>
        <Radio
          value={PAYMENT_METHOD.CREDIT_CARD}
          disabled={!creditCardCheckoutEnabled}
        >
          Credit Card
        </Radio>
      </StyledRadioGroup>
      {values.paymentMethod === PAYMENT_METHOD.PURCHASE_ORDER && (
        <>
          <PaymentDetails>
            <SectionHeader>PAYMENT DETAILS</SectionHeader>
            <FieldColumn>
              <AttachPurchaseOrder>
                {!values.purchaseOrderFile && (
                  <UploadButtonWrapper>
                    <UploadButton
                      variant="secondary"
                      onUpload={(files) => handleAttach(files)}
                      size="md"
                    >
                      <UploadDiskIcon boxSize={2} mr={1} />
                      {drawerMode === DRAWER_MODES.EDIT && order?.poFileUrl
                        ? 'Replace Purchase Order'
                        : 'Upload Purchase Order'}
                    </UploadButton>
                  </UploadButtonWrapper>
                )}
                {values.purchaseOrderFile && (
                  <PurchaseOrderFile>
                    <span>{values.purchaseOrderFile.name}</span>
                    <button
                      type="button"
                      title="Delete purchase order pdf"
                      onClick={handleRemoveAttachment}
                    >
                      <DeleteIcon size="16" />
                    </button>
                  </PurchaseOrderFile>
                )}
              </AttachPurchaseOrder>
              <FieldWrapper>
                <Input
                  field={{
                    name: 'purchaseOrderNumber',
                    value: values.purchaseOrderNumber,
                    onChange: handleChange,
                  }}
                  form={{ touched, errors }}
                  autoComplete="none"
                  name="purchaseOrderNumber"
                  label="Purchase Order #"
                  value={values.purchaseOrderNumber}
                  onChange={handleChange}
                />
              </FieldWrapper>
            </FieldColumn>
          </PaymentDetails>
        </>
      )}
    </form>
  );
});

PaymentMethodForm.displayName = 'PaymentMethodForm';

export default PaymentMethodForm;
